/**
 * Community Hub Mutation Hooks
 *
 * React Query mutation hooks for Community Hub actions
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  acceptTerms,
  createPost,
  updatePost,
  deletePost,
  togglePostLike,
  createComment,
  updateComment,
  deleteComment,
  toggleCommentLike,
  reportPost,
  reportComment,
  updateReport,
  hidePost,
  unhidePost,
  hideComment,
  togglePostPin,
  blockUser,
  unblockUser,
} from '../services/communityApi';
import { communityKeys } from './useCommunity';
import type {
  CreatePostRequest,
  UpdatePostRequest,
  CreateCommentRequest,
  UpdateCommentRequest,
  CreateReportRequest,
  UpdateReportRequest,
  BlockUserRequest,
  CommunityPost,
  PostsResponse,
} from '../types/community';

// ============================================
// TERMS MUTATIONS
// ============================================

/**
 * Hook to accept terms of use
 */
export const useAcceptTerms = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: acceptTerms,
    onSuccess: () => {
      // Invalidate terms status to refresh the check
      queryClient.invalidateQueries({ queryKey: communityKeys.termsStatus() });
    },
  });
};

// ============================================
// POST MUTATIONS
// ============================================

/**
 * Hook to create a new post
 */
export const useCreatePost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePostRequest) => createPost(data),
    onSuccess: () => {
      // Invalidate posts list to show new post
      queryClient.invalidateQueries({ queryKey: communityKeys.posts() });
    },
  });
};

/**
 * Hook to update a post
 */
export const useUpdatePost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePostRequest }) => updatePost(id, data),
    onSuccess: (_, variables) => {
      // Invalidate specific post and posts list
      queryClient.invalidateQueries({ queryKey: communityKeys.postDetail(variables.id) });
      queryClient.invalidateQueries({ queryKey: communityKeys.posts() });
    },
  });
};

/**
 * Hook to delete a post
 */
export const useDeletePost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deletePost(id),
    onSuccess: (_, id) => {
      // Remove from cache and invalidate posts list
      queryClient.removeQueries({ queryKey: communityKeys.postDetail(id) });
      queryClient.invalidateQueries({ queryKey: communityKeys.posts() });
    },
  });
};

/**
 * Hook to toggle like on a post
 */
export const useTogglePostLike = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => togglePostLike(id),
    onMutate: async (id) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: communityKeys.posts() });

      // Snapshot the previous value
      const previousPosts = queryClient.getQueryData(communityKeys.postsList());

      // Optimistically update the post
      queryClient.setQueriesData(
        { queryKey: communityKeys.posts() },
        (old: PostsResponse | undefined) => {
          if (!old) return old;
          return {
            ...old,
            data: {
              ...old.data,
              posts: old.data.posts.map((post: CommunityPost) =>
                post._id === id
                  ? {
                      ...post,
                      isLiked: !post.isLiked,
                      likesCount: post.isLiked ? post.likesCount - 1 : post.likesCount + 1,
                    }
                  : post
              ),
            },
          };
        }
      );

      return { previousPosts };
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousPosts) {
        queryClient.setQueryData(communityKeys.postsList(), context.previousPosts);
      }
    },
    onSettled: () => {
      // Refetch after mutation settles
      queryClient.invalidateQueries({ queryKey: communityKeys.posts() });
    },
  });
};

// ============================================
// COMMENT MUTATIONS
// ============================================

/**
 * Hook to create a comment
 */
export const useCreateComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ postId, data }: { postId: string; data: CreateCommentRequest }) =>
      createComment(postId, data),
    onSuccess: (_, variables) => {
      // Invalidate comments for the post
      queryClient.invalidateQueries({
        queryKey: communityKeys.postComments(variables.postId),
      });
      // Also invalidate posts to update comment count
      queryClient.invalidateQueries({ queryKey: communityKeys.posts() });
    },
  });
};

/**
 * Hook to update a comment
 */
export const useUpdateComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCommentRequest }) =>
      updateComment(id, data),
    onSuccess: () => {
      // Invalidate all comments (we don't know which post it belongs to from here)
      queryClient.invalidateQueries({ queryKey: communityKeys.comments() });
    },
  });
};

/**
 * Hook to delete a comment
 */
export const useDeleteComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteComment(id),
    onSuccess: () => {
      // Invalidate comments and posts (for comment count)
      queryClient.invalidateQueries({ queryKey: communityKeys.comments() });
      queryClient.invalidateQueries({ queryKey: communityKeys.posts() });
    },
  });
};

/**
 * Hook to toggle like on a comment
 */
export const useToggleCommentLike = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => toggleCommentLike(id),
    onSuccess: () => {
      // Invalidate comments to refresh like status
      queryClient.invalidateQueries({ queryKey: communityKeys.comments() });
    },
  });
};

// ============================================
// REPORT MUTATIONS
// ============================================

/**
 * Hook to report a post
 */
export const useReportPost = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateReportRequest }) => reportPost(id, data),
  });
};

/**
 * Hook to report a comment
 */
export const useReportComment = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateReportRequest }) =>
      reportComment(id, data),
  });
};

/**
 * Hook to update a report (Super Admin)
 */
export const useUpdateReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateReportRequest }) => updateReport(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.reports() });
    },
  });
};

// ============================================
// ADMIN MUTATIONS
// ============================================

/**
 * Hook to hide a post (Super Admin)
 */
export const useHidePost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => hidePost(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.posts() });
      queryClient.invalidateQueries({ queryKey: communityKeys.reports() });
    },
  });
};

/**
 * Hook to unhide a post (Super Admin)
 */
export const useUnhidePost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => unhidePost(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.posts() });
    },
  });
};

/**
 * Hook to hide a comment (Super Admin)
 */
export const useHideComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => hideComment(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.comments() });
      queryClient.invalidateQueries({ queryKey: communityKeys.reports() });
    },
  });
};

/**
 * Hook to pin/unpin a post (Super Admin)
 */
export const useTogglePostPin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) => togglePostPin(id, pinned),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.posts() });
    },
  });
};

/**
 * Hook to block a user (Super Admin)
 */
export const useBlockUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: BlockUserRequest }) =>
      blockUser(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.blocks() });
    },
  });
};

/**
 * Hook to unblock a user (Super Admin)
 */
export const useUnblockUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => unblockUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.blocks() });
    },
  });
};

// Export all hooks
export default {
  // Terms
  useAcceptTerms,
  // Posts
  useCreatePost,
  useUpdatePost,
  useDeletePost,
  useTogglePostLike,
  // Comments
  useCreateComment,
  useUpdateComment,
  useDeleteComment,
  useToggleCommentLike,
  // Reports
  useReportPost,
  useReportComment,
  useUpdateReport,
  // Admin
  useHidePost,
  useUnhidePost,
  useHideComment,
  useTogglePostPin,
  useBlockUser,
  useUnblockUser,
};
